import { AppConfigs, AppCriteriaField, AppSkinProps, CriteriaConstraint } from '../../../../types/decisions';
import React, { useEffect, useState } from 'react';
import { Button, Checkbox, FormControl, FormControlLabel, IconButton, MenuItem, Select } from '@mui/material';
import Slider from '../../../slider/Slider';
import { TriangleSlider } from '../../../slider';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DraggableDiv from '../../../draggableDiv/DraggableDiv';
import NameWithIcon from '../NameWithIcon/NameWithIcon';

function renderSelectItems(items: {
        name: string;
        value: number | string;
        icon: string;
    }[],
    hideImportance: boolean,
    itemValues: {
        [p: string]: {
            isChecked: boolean;
            value: string;
        }
    },
    setItemValues: (value: (((prevState: {
        [p: string]: {
            isChecked: boolean;
            value: string;
        }
    }) => {
        [p: string]: {
            isChecked: boolean;
            value: string;
        }
    }) |
                            {
                                [p: string]: {
                                    isChecked: boolean;
                                    value: string;
                                }
                            })) => void,
    onTradeoffValueChange: (value: any, id: string, key: string) => void,
    id: string,
    initialValues: {
        [p: string]: string
    },
) {
    return (
        <>
            {items.map((item, index) => (
                <div
                    key={item.name + index} className="multi-select-settings-item"
                    style={{ width: hideImportance ? '100%' : '48%' }}
                >
                    <div className="custom-slider-label truncate">
                        <FormControlLabel
                            control={<Checkbox defaultChecked={item.value !== '-1'}/>}
                            label={<NameWithIcon icon={item.icon} name={item.name}/>}
                            checked={itemValues[item.name]?.isChecked}
                            onChange={(_, checked) => {
                                setItemValues(items => (
                                    {
                                        ...items,
                                        [item.name]: {
                                            value: checked ? '50' : '-1',
                                            isChecked: checked,
                                        },
                                    }
                                ));
                                if (!checked) {
                                    onTradeoffValueChange('-1', id, item.name);
                                } else {
                                    let defaultValue = initialValues[item.name];
                                    if (defaultValue === '-1') {
                                        defaultValue = '50';
                                    }
                                    onTradeoffValueChange(defaultValue, id, item.name);
                                }
                            }}
                        />
                    </div>
                    <div className="custom-slider">
                        <Slider
                            disabled={!itemValues[item.name]?.isChecked}
                            min={0}
                            max={100}
                            value={+itemValues[item.name]?.value}
                            onChange={(_, value) => {
                                setItemValues(items => (
                                    {
                                        ...items,
                                        [item.name]: {
                                            ...items[item.name],
                                            value: value + '',
                                        },
                                    }
                                ));
                            }}
                            onChangeCommitted={(_, value) => onTradeoffValueChange(value, id, item.name)}
                        />
                    </div>
                </div>
            ))}
        </>
    );
}

function getInitialCriteriaFields(items: {
    name: string;
    value: number | string;
    icon: string
}[]) {
    return items.map((item): AppCriteriaField => {
        return {
            name: item.name,
            description: '',
            style: '',
            min: '',
            max: '',
            type: 'select',
            id: 'string' + item.name,
            importance: item.value,
            suffix: '',
            val: '',
            step: '',
            hide_field: false,
            hide_importance: false,
            icon: '',
            images: [],
            technicalField: false,
            calculate: true,
            show_in_result: true,
        };
    });
}

const AppCriteriaTypeSelect = ({
    importance,
    items,
    hideValues,
    hideImportance,
    onSliderChange,
    onTradeoffValueChange,
    initialValues,
    showAddConstraint,
    setCriteriaConstraints,
    criteriaConstraints,
    id,
    variant = 'Slider',
    isModal = true,
    onCriteriaSettingsOpen = () => {
    },
}: {
    importance: number;
    items: {
        name: string;
        value: number | string;
        icon: string;
    }[];
    hideValues?: boolean;
    showAddConstraint?: boolean;
    hideImportance?: boolean;
    onSliderChange: AppSkinProps['onSliderChange'];
    onTradeoffValueChange: AppSkinProps['onTradeoffValueChange']
    initialValues: {
        [key: string]: string;
    };
    id: string;
    criteriaConstraints: {
        [key: string]: CriteriaConstraint[];
    };
    setCriteriaConstraints: (value: (((prevState: {
        [p: string]: CriteriaConstraint[];
    }) => {
        [p: string]: CriteriaConstraint[];
    }) | {
                                         [p: string]: CriteriaConstraint[];
                                     })) => void;
    variant?: AppConfigs['criteriaSettings']['enumerated'];
    isModal?: boolean;
    onCriteriaSettingsOpen?: (id: string) => void;
}) => {
    const [ itemValues, setItemValues ] = useState<{
        [key: string]: {
            isChecked: boolean;
            value: string;
        }
    }>({});

    useEffect(() => {
        if (!criteriaConstraints[id]) {
            setCriteriaConstraints(criteriaConstraints => (
                {
                    ...criteriaConstraints,
                    [id]: [],
                }
            ));
        }

        const itemsChecked: {
            [key: string]: {
                isChecked: boolean;
                value: string;
            }
        } = {};
        items.forEach(item => {
            itemsChecked[item.name] = {
                isChecked: item.value !== '-1',
                value: item.value as string,
            };
        });
        setItemValues(itemsChecked);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onConstraintChange = (value: string, index: number, key: 'value' | 'rule') => {
        setCriteriaConstraints((constraints) => {
            const updatedConstraints = constraints[id].map((constraint, idx) => {
                if (idx !== index) {
                    return constraint;
                }

                return {
                    ...constraint,
                    [key]: value,
                };
            });

            return {
                ...constraints,
                [id]: updatedConstraints,
            };
        });
    };

    const onConstraintOperatorChange = (value: 'and' | 'or', index: number) => {
        setCriteriaConstraints((constraints) => {
            const updatedConstraints = constraints[id].map((constraint, idx) => {
                if (idx !== index) {
                    return constraint;
                }

                return {
                    ...constraint,
                    operator: value,
                };
            });

            return {
                ...constraints,
                [id]: updatedConstraints,
            };
        });
    };

    const onAddConstraint = () => {
        setCriteriaConstraints(constraints => (
            {
                ...constraints,
                [id]: [ ...constraints[id], {
                    value: items[0].name as string || '',
                    rule: '=',
                    operator: constraints[id].length > 0 ? 'and' : null,
                } ],
            }
        ));
    };

    const onDeleteConstraint = (index: number) => {
        setCriteriaConstraints(constraints => {
            const filtered = constraints[id].filter((_, idx) => idx !== index);
            if (!filtered.length) {
                return {
                    ...constraints,
                    [id]: [],
                };
            }

            filtered[0] = {
                ...filtered[0],
                operator: null,
            };

            return {
                ...constraints,
                [id]: filtered,
            };
        });
    };

    const onSliderChangeWrapper = (newValue: number, id: string) => {
        const itemIndex = items.findIndex(item => id === `string${item.name}`);
        if (itemIndex === -1) {
            return;
        }

        onTradeoffValueChange(newValue, id, items[itemIndex].name);
    };

    const styles = !isModal ? {
        transform: 'scale(0.6)',
        width: '100%',
        height: '100%',
    } : {
        width: 1000,
        height: 550,
    };

    const handleSettingsClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isModal) {
            e.preventDefault();
            e.stopPropagation();
            onCriteriaSettingsOpen(id);
        }
    };

    const slider = renderSelectItems(items, hideImportance || false, itemValues, setItemValues, onTradeoffValueChange, id, initialValues);
    const elements = {
        Slider: slider,
        Wheel: slider,
        Swivel: (
            <div
                style={{
                    margin: '0 auto',
                    ...styles,
                }}
                onClick={handleSettingsClick}
            >
                <DraggableDiv
                    initialCriteriaFields={getInitialCriteriaFields(items)}
                    onSliderChange={onSliderChangeWrapper}
                    isEditable={isModal}
                />
            </div>
        ),
        Wipe: slider,
        Agent: slider,
    };

    return (
        <div>
            {hideValues ? null : (
                <div style={{ marginBottom: 10 }}>
                    <div
                        className="multi-select-settings"
                        style={{
                            display: hideImportance ? 'block' : 'flex',
                            minWidth: 250,
                        }}
                    >
                        {elements[variant]}
                    </div>
                </div>
            )}
            {hideImportance ? null : (
                <div>
                    <TriangleSlider
                        min={0}
                        max={100}
                        step={1}
                        value={importance}
                        leftText="Not Important"
                        rightText="Really Important"
                        onChange={(_, value) => onSliderChange(value as number, id)}
                        size="medium"
                    />
                </div>
            )}
            {!showAddConstraint ? null : (
                <div style={{ marginTop: 20 }}>
                    <div style={{ display: criteriaConstraints[id].length > 0 ? 'block' : 'none' }}>
                        <FormControl sx={{
                            my: 2,
                            minWidth: 120,
                        }} size="small">
                            {criteriaConstraints[id].map((constraint, index) => (
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'flex-start',
                                        alignItems: 'center',
                                        marginBottom: 16,
                                    }}
                                    key={id + '_' + index}
                                >
                                    {constraint.operator && (
                                        <Select
                                            style={{ marginRight: 16 }}
                                            variant="outlined"
                                            value={constraint.operator}
                                            onChange={(e) => onConstraintOperatorChange(e.target.value as ('and' | 'or'), index)}
                                        >
                                            <MenuItem value="and">And</MenuItem>
                                            <MenuItem value="or">Or</MenuItem>
                                        </Select>
                                    )}
                                    <Select
                                        variant="outlined"
                                        value={constraint.rule}
                                        onChange={(e) => onConstraintChange(e.target.value, index, 'rule')}
                                    >
                                        <MenuItem value="=">Is</MenuItem>
                                        <MenuItem value="!=">Is not</MenuItem>
                                    </Select>
                                    <Select
                                        style={{ marginLeft: 16 }}
                                        value={constraint.value}
                                        onChange={(e) => onConstraintChange(e.target.value, index, 'value')}
                                        size="small"
                                        variant="outlined"
                                    >
                                        {items.map(({ name }) => (
                                            <MenuItem value={name} key={name}>{name}</MenuItem>
                                        ))}
                                        <MenuItem value="-">NULL</MenuItem>
                                    </Select>
                                    <IconButton
                                        style={{ marginLeft: '.5rem' }}
                                        onClick={() => onDeleteConstraint(index)}
                                    >
                                        <CloseIcon/>
                                    </IconButton>
                                </div>
                            ))}
                        </FormControl>
                    </div>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon/>}
                        size="small"
                        onClick={onAddConstraint}
                    >
                        Add Constraint
                    </Button>
                </div>
            )}
        </div>
    );
};

export default AppCriteriaTypeSelect;
